import React, { useState, createRef } from 'react';
import { Field, Form } from 'react-final-form';
import { useHistory } from 'react-router-dom';
import { FORM_ERROR } from 'final-form';
import Bluebird from 'bluebird';
import { Grid, Paper, Tooltip, Typography, TextField as TextFieldMaterial } from '@mui/material';
import HelpIcon from '@mui/icons-material/Help';
import snackbarHelper from '../helpers/snackbarHelper';
import * as errorsHelper from '../helpers/errors';
import SubmitWithSendEmailConfirmation from './SubmitWithSendEmailConfirmation';
import OverlayLoader from './OverlayLoader';
import routes from '../routes';
import { formatPhoneNumber } from '../helpers/formatters';
import { Colors } from '../assets/theme/Colors';
import FormGrid from './Common/FormGrid';
import { getFormFieldError, isFormFieldInvalid } from '../helpers/validators';

const MAX_CHARACTERS_LIMIT = 160;

const paperSx = {
  my: 1,
  padding: { xs: 1, sm: 2 },
};

const buttonSx = {
  marginTop: 1,
};

const SendSmsForm = ({ verifiedRecipients, unverifiedRecipients, submitCallback, groupName }) => {
  const formRef = createRef();
  const history = useHistory();
  const [loading, setLoading] = useState(false);

  const validate = values => {
    const errors = {};

    if (!values.body) {
      errors.body = 'Required';
    }

    if (verifiedRecipients.length <= 0) {
      errors.recipients = 'Required';
    }

    return errors;
  };

  const submit = values =>
    Bluebird.try(() => {
      setLoading(true);

      return submitCallback({
        body: values.body,
        users_ids: verifiedRecipients.map(r => r.id),
      });
    })
      .then(() => {
        history.push(routes.rescuers);

        snackbarHelper.success(`SMS sent successfully to ${verifiedRecipients.length} rescuer(s)!`);
      })
      .catch(err => {
        errorsHelper.handleError(err);

        return {
          [FORM_ERROR]: 'POST failed',
        };
      });

  return (
    <OverlayLoader isLoading={loading}>
      <Paper sx={paperSx} data-testid="sms-recipients-paper">
        <Typography variant="h6">
          {groupName
            ? `Recipients (${verifiedRecipients.length}); ${groupName}:`
            : `Recipients (${verifiedRecipients.length}):`}

          <Tooltip arrow placement="top" title="List of users who are going to receive this SMS.">
            <HelpIcon htmlColor={Colors.blue.main} style={{ position: 'relative', top: 5, marginLeft: 5 }} />
          </Tooltip>
        </Typography>

        {verifiedRecipients.length > 0 ? (
          <ul>
            {verifiedRecipients.slice(0, 10).map(r => (
              <li key={r.id}>{`${r.firstname} ${r.lastname} (${formatPhoneNumber(r.phone)})`}</li>
            ))}

            {verifiedRecipients.length > 10 && (
              <li>
                <em>{`+${verifiedRecipients.length - 10} more recipients`}</em>
              </li>
            )}
          </ul>
        ) : (
          <Typography data-testid="no-verified-recipients-help" color="error">
            You don't have any recipients with verified phone# selected therefore you will not be able to send the SMS.
          </Typography>
        )}
      </Paper>

      {unverifiedRecipients.length > 0 && (
        <Paper sx={paperSx} data-testid="sms-ignored-recipients-paper">
          <Typography variant="h6">
            {`Ignored recipients (${unverifiedRecipients.length}):`}

            <Tooltip
              arrow
              placement="top"
              title="List of users who are not going to receive this SMS, because their phone# is not verified."
            >
              <HelpIcon htmlColor={Colors.blue.main} style={{ position: 'relative', top: 5, marginLeft: 5 }} />
            </Tooltip>
          </Typography>

          <ul>
            {unverifiedRecipients.slice(0, 10).map(r => (
              <li key={r.id}>{`${r.firstname} ${r.lastname} (${formatPhoneNumber(r.phone)})`}</li>
            ))}

            {unverifiedRecipients.length > 10 && (
              <li>
                <em>{`+${unverifiedRecipients.length - 10} more ignored recipients`}</em>
              </li>
            )}
          </ul>
        </Paper>
      )}

      <Paper sx={paperSx}>
        <Form
          onSubmit={submit}
          validate={validate}
          render={({ handleSubmit, submitting, pristine, invalid, dirtySinceLastSubmit }) => (
            <form onSubmit={handleSubmit} ref={formRef}>
              <FormGrid>
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <Field
                      name="body"
                      render={({ input, meta }) => {
                        const showError = isFormFieldInvalid(meta);

                        return (
                          <TextFieldMaterial
                            variant="standard"
                            InputProps={{
                              disabled: verifiedRecipients.length === 0,
                            }}
                            fullWidth
                            maxRows={10}
                            multiline
                            required
                            type="text"
                            label="SMS Content"
                            inputProps={{
                              maxLength: MAX_CHARACTERS_LIMIT,
                            }}
                            error={showError}
                            helperText={
                              showError
                                ? getFormFieldError(meta)
                                : `Used ${input.value.length} out of ${MAX_CHARACTERS_LIMIT} characters`
                            }
                            {...input}
                          />
                        );
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <SubmitWithSendEmailConfirmation
                      title="Heads up!"
                      message={`Do you really want to send SMS out to ${verifiedRecipients.length} recipient(s)?`}
                      confirmButtonLabel="Send SMS"
                      label="Send SMS"
                      showConfirmation
                      disabled={pristine || (invalid && !dirtySinceLastSubmit) || submitting}
                      sx={buttonSx}
                      onButtonNotSend={() => true}
                      onButtonSend={() => {
                        formRef.current.dispatchEvent(
                          new Event('submit', {
                            bubbles: true,
                            cancelable: true,
                          })
                        );
                      }}
                      data-testid="send-sms-submit-button"
                    />
                  </Grid>
                </Grid>
              </FormGrid>
            </form>
          )}
        />
      </Paper>
    </OverlayLoader>
  );
};

export default SendSmsForm;
